import { useState, useEffect } from "react";
import { 
  TrendingUp, 
  TrendingDown, 
  Target, 
  AlertTriangle,
  Activity,
  DollarSign,
  Percent,
  Clock,
  RefreshCw,
  Plus,
  Trash2,
  Eye,
  Bell,
  BarChart3,
  Crosshair,
  Shield,
  Zap,
  CheckCircle,
  XCircle,
  Loader2,
  ArrowUpRight,
  ArrowDownRight,
  Settings,
  History,
  Play,
  Pause,
  Info,
  Copy,
  ExternalLink
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PropTrading = () => {
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [ftmoStatus, setFtmoStatus] = useState(null);
  const [instruments, setInstruments] = useState({ FX: [], Index: [], Commodity: [] });
  const [scanResults, setScanResults] = useState(null);
  const [watchlist, setWatchlist] = useState([]);
  const [trades, setTrades] = useState([]);
  const [tvAlerts, setTvAlerts] = useState([]);
  const [selectedTimeframe, setSelectedTimeframe] = useState("1d");
  const [analyzingSetup, setAnalyzingSetup] = useState(false);
  const [setupAnalysis, setSetupAnalysis] = useState(null);
  const [paperTradingMode, setPaperTradingMode] = useState(true);
  const [selectedSetup, setSelectedSetup] = useState(null);
  const [showTradeDialog, setShowTradeDialog] = useState(false);
  const [tradeForm, setTradeForm] = useState({
    symbol: "",
    direction: "BUY",
    entry: "",
    stopLoss: "",
    takeProfit: "",
    riskPct: 1,
    lots: 0.01
  });
  const [positionSize, setPositionSize] = useState(null);
  const [executingTrade, setExecutingTrade] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [statusRes, instrumentsRes, watchlistRes, tradesRes, alertsRes] = await Promise.all([
        axios.get(`${API}/ftmo/status`),
        axios.get(`${API}/ftmo/instruments`),
        axios.get(`${API}/ftmo/watchlist`),
        axios.get(`${API}/ftmo/trades?limit=20`),
        axios.get(`${API}/ftmo/webhook/alerts?limit=10`)
      ]);
      
      setFtmoStatus(statusRes.data);
      setInstruments(instrumentsRes.data.instruments);
      setWatchlist(watchlistRes.data.watchlist || []);
      setTrades(tradesRes.data.trades || []);
      setTvAlerts(alertsRes.data.alerts || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load FTMO data");
    } finally {
      setLoading(false);
    }
  };

  const runMarketScan = async () => {
    setScanning(true);
    setScanResults(null);
    try {
      const response = await axios.get(`${API}/ftmo/scan?timeframe=${selectedTimeframe}`);
      setScanResults(response.data);
      const count = response.data.setups_found || 0;
      if (count > 0) {
        toast.success(`Found ${count} actionable trade setup${count > 1 ? 's' : ''}!`);
      } else {
        toast.info("No high-probability setups found. Markets may be ranging.");
      }
    } catch (error) {
      toast.error("Market scan failed");
    } finally {
      setScanning(false);
    }
  };

  const analyzeAndTrade = async (setup) => {
    setSelectedSetup(setup);
    setAnalyzingSetup(true);
    
    // Pre-fill trade form with suggested values
    const direction = setup.setup_direction;
    const currentPrice = setup.current_price;
    const nearestSupport = setup.nearest_support;
    const nearestResistance = setup.nearest_resistance;
    
    // Calculate suggested entry, SL, TP based on direction
    let suggestedEntry, suggestedSL, suggestedTP;
    
    if (direction === "BUY") {
      suggestedEntry = currentPrice;
      suggestedSL = nearestSupport ? nearestSupport * 0.998 : currentPrice * 0.99; // Below support
      suggestedTP = nearestResistance ? nearestResistance * 0.998 : currentPrice * 1.02; // Near resistance
    } else {
      suggestedEntry = currentPrice;
      suggestedSL = nearestResistance ? nearestResistance * 1.002 : currentPrice * 1.01; // Above resistance
      suggestedTP = nearestSupport ? nearestSupport * 1.002 : currentPrice * 0.98; // Near support
    }
    
    setTradeForm({
      symbol: setup.symbol,
      direction: direction,
      entry: suggestedEntry?.toFixed(5) || "",
      stopLoss: suggestedSL?.toFixed(5) || "",
      takeProfit: suggestedTP?.toFixed(5) || "",
      riskPct: 1,
      lots: 0.01
    });
    
    // Calculate position size
    try {
      const posRes = await axios.post(`${API}/ftmo/position-size`, {
        account_balance: ftmoStatus?.balance || 70000,
        risk_pct: 1,
        entry: suggestedEntry,
        stop_loss: suggestedSL,
        symbol: setup.symbol
      });
      setPositionSize(posRes.data);
      setTradeForm(prev => ({ ...prev, lots: posRes.data.recommended_lots || 0.01 }));
    } catch (e) {
      console.error("Position size calc failed", e);
    }
    
    // Get AI Analysis
    try {
      const response = await axios.post(`${API}/ftmo/analyze-setup`, {
        symbol: setup.symbol,
        direction: direction,
        entry: suggestedEntry,
        stop_loss: suggestedSL,
        take_profit: suggestedTP
      });
      setSetupAnalysis(response.data);
    } catch (error) {
      toast.error("AI analysis failed");
    }
    
    setAnalyzingSetup(false);
    setShowTradeDialog(true);
  };

  const executePaperTrade = async () => {
    if (!tradeForm.symbol || !tradeForm.entry) {
      toast.error("Please fill in trade details");
      return;
    }
    
    setExecutingTrade(true);
    
    try {
      // Simulate trade execution in paper mode
      const riskReward = Math.abs(parseFloat(tradeForm.takeProfit) - parseFloat(tradeForm.entry)) / 
                         Math.abs(parseFloat(tradeForm.entry) - parseFloat(tradeForm.stopLoss));
      
      // Simulate random outcome based on win rate and setup score
      const winProbability = (ftmoStatus?.win_rate || 50) / 100;
      const isWin = Math.random() < winProbability;
      
      const pnl = isWin 
        ? Math.abs(parseFloat(tradeForm.takeProfit) - parseFloat(tradeForm.entry)) * tradeForm.lots * 100000 * 0.1
        : -Math.abs(parseFloat(tradeForm.entry) - parseFloat(tradeForm.stopLoss)) * tradeForm.lots * 100000 * 0.1;
      
      await axios.post(`${API}/ftmo/trades`, {
        symbol: tradeForm.symbol,
        direction: tradeForm.direction,
        entry: parseFloat(tradeForm.entry),
        exit: isWin ? parseFloat(tradeForm.takeProfit) : parseFloat(tradeForm.stopLoss),
        stop_loss: parseFloat(tradeForm.stopLoss),
        take_profit: parseFloat(tradeForm.takeProfit),
        pnl: pnl,
        lots: tradeForm.lots,
        notes: `Paper trade - ${setupAnalysis?.orchestrator?.verdict || 'N/A'} verdict`
      });
      
      toast.success(
        isWin 
          ? `✅ Paper Trade WIN! +£${Math.abs(pnl).toFixed(2)}` 
          : `❌ Paper Trade LOSS: -£${Math.abs(pnl).toFixed(2)}`
      );
      
      setShowTradeDialog(false);
      setSelectedSetup(null);
      setSetupAnalysis(null);
      fetchAllData();
      
    } catch (error) {
      toast.error("Failed to execute paper trade");
    } finally {
      setExecutingTrade(false);
    }
  };

  const addToWatchlist = async (symbol) => {
    try {
      await axios.post(`${API}/ftmo/watchlist`, { symbol });
      fetchAllData();
      toast.success(`${symbol} added to watchlist`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add to watchlist");
    }
  };

  const removeFromWatchlist = async (symbol) => {
    try {
      await axios.delete(`${API}/ftmo/watchlist/${symbol}`);
      setWatchlist(watchlist.filter(w => w.symbol !== symbol));
      toast.success(`${symbol} removed`);
    } catch (error) {
      toast.error("Failed to remove");
    }
  };

  const getRiskColor = (level) => {
    switch(level) {
      case "LOW": return "text-success border-success";
      case "MODERATE": return "text-warning border-warning";
      case "HIGH": return "text-orange-500 border-orange-500";
      case "CRITICAL": return "text-destructive border-destructive";
      default: return "text-muted-foreground";
    }
  };

  const getWebhookUrl = () => `${process.env.REACT_APP_BACKEND_URL}/api/ftmo/webhook/tradingview`;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center space-y-4">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground">Loading FTMO dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-6 p-6" data-testid="prop-trading-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Crosshair className="w-6 h-6 text-primary" />
              FTMO Prop Trading
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              AI-powered trade setups with clear entry, stop loss & take profit levels
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Paper Trading Toggle */}
            <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg">
              <Switch
                checked={paperTradingMode}
                onCheckedChange={setPaperTradingMode}
                data-testid="paper-trading-toggle"
              />
              <span className={`text-sm font-medium ${paperTradingMode ? "text-warning" : "text-success"}`}>
                {paperTradingMode ? "📝 Paper Mode" : "🔴 Live Mode"}
              </span>
            </div>
            <Badge variant="outline" className={getRiskColor(ftmoStatus?.risk_level)}>
              Risk: {ftmoStatus?.risk_level || "N/A"}
            </Badge>
            <Button variant="outline" size="sm" onClick={fetchAllData}>
              <RefreshCw className="w-4 h-4 mr-1" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Paper Trading Info Banner */}
        {paperTradingMode && (
          <Alert className="bg-warning/10 border-warning/30">
            <Info className="w-4 h-4 text-warning" />
            <AlertTitle className="text-warning">Paper Trading Mode Active</AlertTitle>
            <AlertDescription className="text-sm">
              Trades are simulated. Use this to practice strategies risk-free before your real FTMO challenge.
            </AlertDescription>
          </Alert>
        )}

        {/* FTMO Challenge Status Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card className="bg-gradient-to-br from-primary/10 to-primary/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                <DollarSign className="w-3 h-3" />
                Balance
              </div>
              <p className="text-xl font-bold">£{ftmoStatus?.balance?.toLocaleString() || "70,000"}</p>
            </CardContent>
          </Card>

          <Card className={ftmoStatus?.current_pnl >= 0 ? "bg-success/10" : "bg-destructive/10"}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                {ftmoStatus?.current_pnl >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                P&L
              </div>
              <p className={`text-xl font-bold ${ftmoStatus?.current_pnl >= 0 ? "text-success" : "text-destructive"}`}>
                {ftmoStatus?.current_pnl >= 0 ? "+" : ""}£{ftmoStatus?.current_pnl?.toFixed(2) || "0.00"}
              </p>
              <p className="text-xs text-muted-foreground">
                ({ftmoStatus?.current_pnl_pct?.toFixed(2) || "0.00"}%)
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                <Target className="w-3 h-3" />
                To Target
              </div>
              <p className="text-xl font-bold">£{ftmoStatus?.remaining_to_target?.toFixed(2) || "7,000"}</p>
              <Progress value={100 - (ftmoStatus?.remaining_to_target_pct || 100)} className="h-1 mt-2" />
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                <AlertTriangle className="w-3 h-3" />
                Daily Loss Left
              </div>
              <p className="text-xl font-bold">£{ftmoStatus?.max_daily_loss_remaining?.toFixed(2) || "3,500"}</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                <Percent className="w-3 h-3" />
                Win Rate
              </div>
              <p className="text-xl font-bold">{ftmoStatus?.win_rate?.toFixed(1) || "50"}%</p>
              <p className="text-xs text-muted-foreground">
                {ftmoStatus?.winning_trades || 0}W / {ftmoStatus?.losing_trades || 0}L
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                <Clock className="w-3 h-3" />
                Trading Days
              </div>
              <p className="text-xl font-bold">{ftmoStatus?.trading_days || 0}</p>
              <p className="text-xs text-muted-foreground">Min: 4 days</p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="scanner" className="space-y-4">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="scanner" data-testid="tab-scanner">
              <Zap className="w-4 h-4 mr-1" /> Find Trades
            </TabsTrigger>
            <TabsTrigger value="watchlist" data-testid="tab-watchlist">
              <Eye className="w-4 h-4 mr-1" /> Watchlist
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">
              <History className="w-4 h-4 mr-1" /> History
            </TabsTrigger>
            <TabsTrigger value="tv-alerts" data-testid="tab-tv-alerts">
              <Bell className="w-4 h-4 mr-1" /> TV Alerts
            </TabsTrigger>
            <TabsTrigger value="howto" data-testid="tab-howto">
              <Info className="w-4 h-4 mr-1" /> How To Use
            </TabsTrigger>
          </TabsList>

          {/* Scanner Tab */}
          <TabsContent value="scanner" className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">Trade Setup Scanner</CardTitle>
                    <CardDescription>Scans FX, Indices & Commodities for high-probability setups</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Select value={selectedTimeframe} onValueChange={setSelectedTimeframe}>
                      <SelectTrigger className="w-28">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1d">Daily (Swing)</SelectItem>
                        <SelectItem value="1h">Hourly (Day)</SelectItem>
                        <SelectItem value="15m">15min (Scalp)</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button onClick={runMarketScan} disabled={scanning} data-testid="scan-markets-btn" className="min-w-[140px]">
                      {scanning ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          Scanning...
                        </>
                      ) : (
                        <>
                          <Zap className="w-4 h-4 mr-1" />
                          Scan Markets
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {scanResults ? (
                  <div className="space-y-6">
                    <div className="flex items-center gap-4 text-sm text-muted-foreground bg-muted/30 p-3 rounded-lg">
                      <span>✅ Scanned: {scanResults.total_scanned} instruments</span>
                      <span>🎯 Setups found: {scanResults.setups_found}</span>
                      <span>⏱️ Timeframe: {scanResults.timeframe === "1d" ? "Daily" : scanResults.timeframe === "1h" ? "Hourly" : "15min"}</span>
                    </div>

                    {/* Actionable Trade Setups */}
                    {(scanResults.buy_setups?.length > 0 || scanResults.sell_setups?.length > 0) ? (
                      <div className="space-y-6">
                        {/* BUY Setups */}
                        {scanResults.buy_setups?.length > 0 && (
                          <div>
                            <h4 className="font-semibold text-success flex items-center gap-2 mb-3 text-lg">
                              <ArrowUpRight className="w-5 h-5" /> BUY Opportunities
                            </h4>
                            <div className="grid md:grid-cols-2 gap-4">
                              {scanResults.buy_setups.map((setup) => (
                                <TradeSetupCard
                                  key={setup.symbol}
                                  setup={setup}
                                  onTrade={() => analyzeAndTrade(setup)}
                                  onWatchlist={() => addToWatchlist(setup.symbol)}
                                />
                              ))}
                            </div>
                          </div>
                        )}

                        {/* SELL Setups */}
                        {scanResults.sell_setups?.length > 0 && (
                          <div>
                            <h4 className="font-semibold text-destructive flex items-center gap-2 mb-3 text-lg">
                              <ArrowDownRight className="w-5 h-5" /> SELL Opportunities
                            </h4>
                            <div className="grid md:grid-cols-2 gap-4">
                              {scanResults.sell_setups.map((setup) => (
                                <TradeSetupCard
                                  key={setup.symbol}
                                  setup={setup}
                                  onTrade={() => analyzeAndTrade(setup)}
                                  onWatchlist={() => addToWatchlist(setup.symbol)}
                                />
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-12 bg-muted/20 rounded-lg">
                        <Activity className="w-12 h-12 mx-auto mb-3 opacity-50 text-muted-foreground" />
                        <p className="font-medium">No High-Probability Setups Found</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Markets may be ranging or consolidating. Try a different timeframe or check back later.
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-16 bg-muted/20 rounded-lg">
                    <Zap className="w-16 h-16 mx-auto mb-4 text-primary opacity-70" />
                    <h3 className="text-lg font-semibold mb-2">Ready to Find Trade Setups</h3>
                    <p className="text-muted-foreground mb-4 max-w-md mx-auto">
                      Click "Scan Markets" to analyze 20 instruments and find trade opportunities with clear entry, stop loss, and take profit levels.
                    </p>
                    <Button onClick={runMarketScan} size="lg" data-testid="scan-markets-btn-hero">
                      <Zap className="w-5 h-5 mr-2" />
                      Scan Markets Now
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Watchlist Tab */}
          <TabsContent value="watchlist">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Watchlist</CardTitle>
                <CardDescription>Instruments you're monitoring for setups</CardDescription>
              </CardHeader>
              <CardContent>
                {watchlist.length > 0 ? (
                  <div className="space-y-2">
                    {watchlist.map((item) => (
                      <div key={item.id} className="flex items-center justify-between p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <Activity className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-semibold">{item.symbol}</p>
                            <p className="text-sm text-muted-foreground">{item.name}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{item.type}</Badge>
                          <Button variant="ghost" size="icon" onClick={() => removeFromWatchlist(item.symbol)}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Eye className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p className="font-medium">No instruments in watchlist</p>
                    <p className="text-sm mt-1">Add instruments from the Scanner tab using the + button</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Trade History</CardTitle>
                <CardDescription>Your {paperTradingMode ? "paper" : "live"} trading journal</CardDescription>
              </CardHeader>
              <CardContent>
                {trades.length > 0 ? (
                  <div className="space-y-2">
                    {trades.map((trade) => (
                      <div key={trade.id} className={`flex items-center justify-between p-4 rounded-lg border-l-4 ${
                        trade.result === "WIN" 
                          ? "bg-success/5 border-success" 
                          : "bg-destructive/5 border-destructive"
                      }`}>
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            trade.result === "WIN" ? "bg-success/20" : "bg-destructive/20"
                          }`}>
                            {trade.result === "WIN" ? (
                              <CheckCircle className="w-5 h-5 text-success" />
                            ) : (
                              <XCircle className="w-5 h-5 text-destructive" />
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-semibold">{trade.symbol}</p>
                              <Badge variant={trade.direction === "BUY" ? "default" : "destructive"} className="text-xs">
                                {trade.direction}
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground">
                              Entry: {trade.entry?.toFixed(5)} → Exit: {trade.exit?.toFixed(5)}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`font-bold text-lg ${trade.pnl >= 0 ? "text-success" : "text-destructive"}`}>
                            {trade.pnl >= 0 ? "+" : ""}£{Math.abs(trade.pnl)?.toFixed(2)}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(trade.timestamp).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p className="font-medium">No trades logged yet</p>
                    <p className="text-sm mt-1">Start by scanning for setups and executing trades</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* TV Alerts Tab */}
          <TabsContent value="tv-alerts">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">TradingView Alerts</CardTitle>
                    <CardDescription>Receive signals from your TradingView alerts</CardDescription>
                  </div>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm">
                        <Settings className="w-4 h-4 mr-1" />
                        Setup Webhook
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg">
                      <DialogHeader>
                        <DialogTitle>TradingView Webhook Setup</DialogTitle>
                        <DialogDescription>
                          Follow these steps to connect TradingView alerts to this app
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label>Step 1: Copy this Webhook URL</Label>
                          <div className="flex gap-2">
                            <Input value={getWebhookUrl()} readOnly className="font-mono text-xs" />
                            <Button 
                              variant="outline" 
                              size="icon"
                              onClick={() => {
                                navigator.clipboard.writeText(getWebhookUrl());
                                toast.success("Copied!");
                              }}
                            >
                              <Copy className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Step 2: In TradingView</Label>
                          <ol className="list-decimal list-inside text-sm space-y-1 text-muted-foreground">
                            <li>Create an alert on your chart</li>
                            <li>Go to "Notifications" tab</li>
                            <li>Enable "Webhook URL"</li>
                            <li>Paste the URL above</li>
                          </ol>
                        </div>
                        <div className="space-y-2">
                          <Label>Step 3: Use this message format</Label>
                          <pre className="p-3 bg-muted rounded-lg text-xs overflow-auto">
{`{
  "ticker": "{{ticker}}",
  "action": "{{strategy.order.action}}",
  "price": {{close}},
  "interval": "{{interval}}"
}`}
                          </pre>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {tvAlerts.length > 0 ? (
                  <div className="space-y-2">
                    {tvAlerts.map((alert) => (
                      <div key={alert.id} className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <Bell className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-semibold">{alert.symbol}</p>
                            <p className="text-sm text-muted-foreground">{alert.action || alert.message}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">{alert.price}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(alert.timestamp).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Bell className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p className="font-medium">No alerts received yet</p>
                    <p className="text-sm mt-1">Set up the webhook in TradingView to start receiving alerts</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* How To Use Tab */}
          <TabsContent value="howto">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">How To Use This Tool</CardTitle>
                <CardDescription>A simple guide to finding and executing trades</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h4 className="font-semibold flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-sm">1</span>
                      Scan for Trade Setups
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Click "Scan Markets" to analyze 20 instruments (FX pairs, indices, commodities). 
                      The scanner looks for technical patterns like support/resistance bounces, trend continuation, 
                      and candlestick signals.
                    </p>
                  </div>
                  
                  <div className="space-y-4">
                    <h4 className="font-semibold flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-sm">2</span>
                      Review the Setup
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Each setup shows: <strong>Direction</strong> (BUY or SELL), <strong>Entry price</strong>, 
                      <strong>Stop Loss</strong> level, <strong>Take Profit</strong> target, and a 
                      <strong>Score</strong> (higher = stronger signal).
                    </p>
                  </div>
                  
                  <div className="space-y-4">
                    <h4 className="font-semibold flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-sm">3</span>
                      Get AI Analysis
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Click "Trade" to get Scout/Skeptic/Orchestrator verdict. They analyze risk, probability, 
                      and give a final <strong>TAKE</strong>, <strong>WAIT</strong>, or <strong>SKIP</strong> recommendation.
                    </p>
                  </div>
                  
                  <div className="space-y-4">
                    <h4 className="font-semibold flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-sm">4</span>
                      Execute Trade
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      In <strong>Paper Mode</strong>: Practice risk-free with simulated trades.
                      In <strong>Live Mode</strong>: Copy the entry/SL/TP to your MT4/MT5 platform and execute manually.
                    </p>
                  </div>
                </div>

                <Alert>
                  <AlertTriangle className="w-4 h-4" />
                  <AlertTitle>FTMO Risk Management</AlertTitle>
                  <AlertDescription>
                    <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
                      <li>Never risk more than 1-2% per trade</li>
                      <li>Always use stop losses - no exceptions</li>
                      <li>Check daily loss remaining before each trade</li>
                      <li>Minimum 4 trading days required to pass</li>
                    </ul>
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Trade Dialog */}
        <Dialog open={showTradeDialog} onOpenChange={setShowTradeDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {tradeForm.direction === "BUY" ? (
                  <ArrowUpRight className="w-5 h-5 text-success" />
                ) : (
                  <ArrowDownRight className="w-5 h-5 text-destructive" />
                )}
                {tradeForm.direction} {tradeForm.symbol}
              </DialogTitle>
              <DialogDescription>
                Review the trade setup and AI analysis before executing
              </DialogDescription>
            </DialogHeader>
            
            {analyzingSetup ? (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
                <p className="text-muted-foreground">Getting AI analysis...</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Trade Levels */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-3 bg-muted/50 rounded-lg text-center">
                    <p className="text-xs text-muted-foreground mb-1">Entry</p>
                    <p className="font-mono font-bold">{tradeForm.entry}</p>
                  </div>
                  <div className="p-3 bg-destructive/10 rounded-lg text-center">
                    <p className="text-xs text-destructive mb-1">Stop Loss</p>
                    <p className="font-mono font-bold text-destructive">{tradeForm.stopLoss}</p>
                  </div>
                  <div className="p-3 bg-success/10 rounded-lg text-center">
                    <p className="text-xs text-success mb-1">Take Profit</p>
                    <p className="font-mono font-bold text-success">{tradeForm.takeProfit}</p>
                  </div>
                </div>

                {/* Position Size */}
                {positionSize && (
                  <div className="p-3 bg-primary/5 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Recommended Position Size</span>
                      <span className="font-bold">{positionSize.recommended_lots?.toFixed(2)} lots</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Risk: £{positionSize.risk_amount?.toFixed(2)} ({tradeForm.riskPct}% of account)
                    </p>
                  </div>
                )}

                {/* AI Analysis */}
                {setupAnalysis && (
                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm">AI Analysis</h4>
                    
                    {/* Scout */}
                    <div className="p-3 bg-success/5 rounded-lg border border-success/20">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-success text-sm flex items-center gap-1">
                          <TrendingUp className="w-4 h-4" /> Scout
                        </span>
                        <Badge variant="outline">{setupAnalysis.scout?.opportunity_score || 50}/100</Badge>
                      </div>
                      <p className="text-xs">{setupAnalysis.scout?.reasoning || setupAnalysis.scout?.raw || "Analyzing opportunity..."}</p>
                    </div>

                    {/* Skeptic */}
                    <div className="p-3 bg-destructive/5 rounded-lg border border-destructive/20">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-destructive text-sm flex items-center gap-1">
                          <Shield className="w-4 h-4" /> Skeptic
                        </span>
                        <Badge variant="outline" className={
                          setupAnalysis.skeptic?.risk_level === "LOW" ? "text-success" :
                          setupAnalysis.skeptic?.risk_level === "MEDIUM" ? "text-warning" : "text-destructive"
                        }>
                          {setupAnalysis.skeptic?.risk_level || "MEDIUM"} Risk
                        </Badge>
                      </div>
                      <p className="text-xs">{setupAnalysis.skeptic?.concerns?.join(", ") || setupAnalysis.skeptic?.raw || "Reviewing risk..."}</p>
                    </div>

                    {/* Orchestrator Verdict */}
                    <div className={`p-4 rounded-lg border-2 ${
                      setupAnalysis.orchestrator?.verdict === "TAKE" ? "bg-success/10 border-success" :
                      setupAnalysis.orchestrator?.verdict === "SKIP" ? "bg-destructive/10 border-destructive" :
                      "bg-warning/10 border-warning"
                    }`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-bold flex items-center gap-2">
                          <Crosshair className="w-5 h-5" /> Final Verdict
                        </span>
                        <Badge className={
                          setupAnalysis.orchestrator?.verdict === "TAKE" ? "bg-success" :
                          setupAnalysis.orchestrator?.verdict === "SKIP" ? "bg-destructive" : "bg-warning"
                        }>
                          {setupAnalysis.orchestrator?.verdict || "WAIT"}
                        </Badge>
                      </div>
                      <p className="text-sm">{setupAnalysis.orchestrator?.reasoning || setupAnalysis.orchestrator?.raw || "Making decision..."}</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            <DialogFooter className="flex gap-2">
              <Button variant="outline" onClick={() => setShowTradeDialog(false)}>
                Cancel
              </Button>
              {paperTradingMode ? (
                <Button 
                  onClick={executePaperTrade} 
                  disabled={executingTrade || analyzingSetup}
                  className="bg-warning hover:bg-warning/90 text-black"
                >
                  {executingTrade ? (
                    <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Simulating...</>
                  ) : (
                    <><Play className="w-4 h-4 mr-1" /> Execute Paper Trade</>
                  )}
                </Button>
              ) : (
                <Button 
                  variant="default"
                  onClick={() => {
                    const text = `${tradeForm.direction} ${tradeForm.symbol}\nEntry: ${tradeForm.entry}\nSL: ${tradeForm.stopLoss}\nTP: ${tradeForm.takeProfit}\nLots: ${tradeForm.lots}`;
                    navigator.clipboard.writeText(text);
                    toast.success("Trade details copied! Paste into MT4/MT5");
                    setShowTradeDialog(false);
                  }}
                >
                  <Copy className="w-4 h-4 mr-1" /> Copy to Execute
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
};

// Trade Setup Card Component - Much clearer with actionable info
const TradeSetupCard = ({ setup, onTrade, onWatchlist }) => {
  const isLong = setup.setup_direction === "BUY";
  
  // Use suggested_sl and suggested_tp from backend (always populated now)
  const stopLoss = setup.suggested_sl || (isLong ? setup.nearest_support : setup.nearest_resistance) || setup.current_price * (isLong ? 0.99 : 1.01);
  const takeProfit = setup.suggested_tp || (isLong ? setup.nearest_resistance : setup.nearest_support) || setup.current_price * (isLong ? 1.02 : 0.98);
  
  // Calculate R:R ratio
  const slDistance = Math.abs(setup.current_price - stopLoss);
  const tpDistance = Math.abs(takeProfit - setup.current_price);
  const riskReward = slDistance > 0 ? (tpDistance / slDistance).toFixed(1) : "1.0";
  
  return (
    <Card className={`overflow-hidden ${isLong ? "border-success/30" : "border-destructive/30"}`}>
      <div className={`h-1 ${isLong ? "bg-success" : "bg-destructive"}`} />
      <CardContent className="p-4 space-y-3">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isLong ? "bg-success/20" : "bg-destructive/20"}`}>
              {isLong ? <ArrowUpRight className="w-4 h-4 text-success" /> : <ArrowDownRight className="w-4 h-4 text-destructive" />}
            </div>
            <div>
              <p className="font-bold">{setup.symbol}</p>
              <p className="text-xs text-muted-foreground">{setup.name}</p>
            </div>
          </div>
          <Badge variant="outline">{setup.type}</Badge>
        </div>

        {/* Action - What to do */}
        <div className={`p-2 rounded text-center font-semibold ${isLong ? "bg-success/10 text-success" : "bg-destructive/10 text-destructive"}`}>
          {isLong ? "📈 BUY OPPORTUNITY" : "📉 SELL OPPORTUNITY"}
        </div>

        {/* Key Levels - Always show values */}
        <div className="grid grid-cols-3 gap-2 text-xs">
          <div className="text-center p-2 bg-muted/30 rounded">
            <p className="text-muted-foreground mb-1">Entry</p>
            <p className="font-mono font-bold">{setup.current_price?.toFixed(4)}</p>
          </div>
          <div className="text-center p-2 bg-destructive/10 rounded">
            <p className="text-destructive mb-1">Stop Loss</p>
            <p className="font-mono font-bold text-destructive">{stopLoss?.toFixed(4)}</p>
          </div>
          <div className="text-center p-2 bg-success/10 rounded">
            <p className="text-success mb-1">Take Profit</p>
            <p className="font-mono font-bold text-success">{takeProfit?.toFixed(4)}</p>
          </div>
        </div>

        {/* Metrics */}
        <div className="flex items-center justify-between text-xs bg-muted/50 p-2 rounded">
          <span>Score: <strong>{setup.setup_score}/100</strong></span>
          <span>R:R: <strong className="text-primary">{riskReward}:1</strong></span>
          <span>Trend: <strong>{setup.trend?.trend}</strong></span>
        </div>

        {/* Patterns */}
        {setup.patterns?.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {setup.patterns.map((p, i) => (
              <Badge key={i} variant="secondary" className="text-[10px]">
                {p.name} ({p.direction})
              </Badge>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          <Button size="sm" className="flex-1" onClick={onTrade}>
            <Crosshair className="w-4 h-4 mr-1" /> Trade
          </Button>
          <Button size="sm" variant="outline" onClick={onWatchlist}>
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default PropTrading;
